package org.lennycodes.hldi.styles

import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.scale
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.Transition
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.lennycodes.hldi.constants.HLDITheme

@OptIn(ExperimentalComposeWebApi::class)
// ============================================================================
// STYLES
// ============================================================================

val TeamCardStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(HLDITheme.Colors.white)
            .borderRadius(16.px)
            .overflow(Overflow.Hidden)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0.06f))
            .boxShadow(
                offsetX = 0.px, offsetY = 2.px, blurRadius = 8.px,
                color = Color.rgba(15, 23, 42, 0.06f) // subtle, tied to your NAVY tone
            )
            .textAlign(TextAlign.Center)
            .transition(
              transition =  com.varabyte.kobweb.compose.css.Transition .of(
                    property = "all",
                    duration = 400.ms,
                    timingFunction = TransitionTimingFunction.cubicBezier(0.22, 1.0, 0.36, 1.0)
                )
            )
    }
    hover {
        Modifier
            .translateY((-6).px)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0.04f))
            .boxShadow(
                offsetX = 0.px, offsetY = 20.px, blurRadius = 32.px,
                color = Color.rgba(15, 23, 42, 0.12f)
            )
    }
    focusVisible {
        Modifier
            .translateY((-6).px)
            .boxShadow(
                offsetX = 0.px, offsetY = 20.px, blurRadius = 32.px,
                color = Color.rgba(15, 23, 42, 0.12f)
            )
            .outline(2.px, LineStyle.Solid, HLDITheme.Colors.accent)
    }
    cssRule(":hover img") {
        Modifier.scale(1.06)
    }
    // Accent underline beneath the role text grows in on card hover —
    // targets a nested class rather than a raw tag so it doesn't
    // accidentally match anything else inside the card.
    cssRule(":hover .team-card-role-underline") {
        Modifier.styleModifier {
            property("width", "32px")
        }
    }
}


val TeamCardLeadershipBadgeStyle = CssStyle {
    base {
        Modifier
            .position(Position.Absolute)
            .styleModifier {
                property("top", "12px")
                property("right", "12px")
            }
            .borderRadius(999.px)
            .backgroundColor(HLDITheme.Colors.secondary)
            .color(HLDITheme.Colors.white)
            .fontSize(11.px)
            .fontWeight(700)
            .styleModifier {
                property("letter-spacing", "0.04em")
                property("text-transform", "uppercase")
            }
    }
}

val TeamCardContentStyle = CssStyle {

}

val TeamCardNameStyle = CssStyle {
    base {
        Modifier
            .fontSize(20.px)
            .color(HLDITheme.Colors.text)
            .margin(bottom = 8.px)
            .fontFamily("Playfair Display", "serif")
    }
}

val TeamCardRoleStyle = CssStyle {
    base {
        Modifier
            .fontSize(14.px)
            .color(HLDITheme.Colors.secondary)
            .fontWeight(600)
            .margin(bottom = 12.px)
            .styleModifier {
                property("display", "inline-block")
            }
    }
}

val TeamCardRoleUnderlineStyle = CssStyle {
    base {
        Modifier
            .height(2.px)
            .width(0.px)
            .margin(topBottom = 6.px, leftRight = 0.px)
            .backgroundColor(HLDITheme.Colors.accent)
            .styleModifier {
                property("margin-left", "auto")
                property("margin-right", "auto")
            }
            .transition(
               transition =  com.varabyte.kobweb.compose.css.Transition.of(
                    property = "width",
                    duration = 350.ms,
                    timingFunction = TransitionTimingFunction.Ease
                )
            )
    }
}

val TeamCardBioStyle = CssStyle {
    base {
        Modifier
            .fontSize(13.px)
            .color(HLDITheme.Colors.mediumGray)
            .lineHeight(1.6)
            .margin(bottom=16.px)
    }
}
val ViewProfileButtonStyle = CssStyle {

    base {

        Modifier
            .backgroundColor(HLDITheme.Colors.primary)
            .color(Colors.White)

            .borderRadius(topLeft = 8.px, topRight = 8.px)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .textDecorationLine(TextDecorationLine.None)
            .transition(
                com.varabyte.kobweb.compose.css.Transition.of(
                    property = "all",
                    duration = 250.ms,
                    timingFunction = TransitionTimingFunction.Ease
                )
            )

    }

    hover {

        Modifier
            .styleModifier {

                property(
                    "transform",
                    "translateY(-2px)"
                )

                property(
                    "box-shadow",
                    "0 6px 15px rgba(0,0,0,0.15)"
                )

            }

    }

}
val TeamCardDepartmentStyle = CssStyle {

    base {

        Modifier
            .fontSize(13.px)
            .color(HLDITheme.Colors.mediumGray)
            .margin(top = 6.px)

    }

}
val TeamCardImageWrapperStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(28.px, 0.px, 0.px)
            .display(DisplayStyle.Flex)
            .styleModifier {
                property("justify-content", "center")
            }
    }
}

val TeamCardImageStyle = CssStyle {
    base {
        Modifier
            .width(200.px)
            .height(200.px)
            .borderRadius(50.percent)
            .objectFit(ObjectFit.Cover)
            .styleModifier {
                property("object-position", "center top") // keeps faces centered rather than cropping mid-face
                property("border", "4px solid ${HLDITheme.Colors.white}")
                property("box-shadow", "0 4px 16px ${Color.rgba(0, 0, 0, 0.15f)}")
            }
            .transition(
                com.varabyte.kobweb.compose.css.Transition.of(
                    property = "transform",
                    duration = 500.ms,
                    timingFunction = TransitionTimingFunction.cubicBezier(0.22, 1.0, 0.36, 1.0)
                )
            )
    }
}